import React, { useState, useCallback } from "react";
import {
  Modal,
  Box,
  Typography,
  Card,
  CardContent,
  Fade,
  Zoom,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import AButton from "../UI/Inputs/MUI/ATextField/AButton";
import IconProfessional from "../../assets/images/icons/professional.svg";
import IconPatient from "../../assets/images/icons/patient.svg";
import IconPersonal from "../../assets/images/icons/personal.svg";
import { PROFILE_TYPES, PROFILE_OPTIONS, getProfileLabel } from "../../constants/profile";
import { useInvalidateUserCache } from "@hooks/useUserInfos";

const PROFILE_ICONS = {
  [PROFILE_TYPES.PROFESSIONAL]: IconProfessional,
  [PROFILE_TYPES.PATIENT]: IconPatient,
  [PROFILE_TYPES.PERSONAL]: IconPersonal,
};

const STEP_PROFILE = 0;
const STEP_CONFIRMATION = 1;

// Conector customizado para o Stepper (linha roxa)
const PurpleConnector = styled(StepConnector)(() => ({
  "& .MuiStepConnector-line": {
    borderColor: "#7B1FA2",
    borderTopWidth: 2,
  },
}));

// StepLabel customizado
const StyledStepLabel = styled(StepLabel)(() => ({
  "& .MuiStepLabel-label": {
    fontSize: "12px",
    color: "#9E9E9E",
    marginTop: "8px",
    "&.Mui-active": {
      color: "#7B1FA2",
      fontWeight: 600,
    },
    "&.Mui-completed": {
      color: "#7B1FA2",
    },
  },
  "& .MuiStepLabel-iconContainer": {
    "& .MuiStepIcon-root": {
      color: "#E0E0E0",
      width: 20,
      height: 20,
      "&.Mui-active": {
        color: "#7B1FA2",
      },
      "&.Mui-completed": {
        color: "#7B1FA2",
      },
    },
  },
}));

// Container do modal
const ModalBox = styled(Box)(() => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: "850px",
  maxHeight: "90vh",
  outline: "none",
}));

const ProfileUpdateModal = ({
  open,
  onClose,
  currentProfile,
  onProfileUpdate,
  onFinish,
}) => {
  const [activeStep, setActiveStep] = useState(STEP_PROFILE);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const steps = ["Perfil", "Dados"];
  const invalidateUserCache = useInvalidateUserCache();
  

  const profileOptions = PROFILE_OPTIONS.map((option) => ({
    ...option,
    icon: PROFILE_ICONS[option.key],
  }));

  const handleClose = useCallback(() => {
    setActiveStep(STEP_PROFILE);
    setSelectedProfile(null);
    setIsLoading(false);
    onClose();
  }, [onClose]);

  const handleProfileSelect = useCallback((profileKey) => {
    setSelectedProfile(profileKey);
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!selectedProfile) return;

    if (selectedProfile === currentProfile) {
      handleClose();
      return;
    }

    setIsLoading(true);

    try {
      if (onProfileUpdate) {
        await onProfileUpdate(selectedProfile);
      }
      setActiveStep(STEP_CONFIRMATION);
      invalidateUserCache();
    } catch (error) {
      console.error("Erro ao atualizar perfil:", error);
    } finally {
      setIsLoading(false);
      window.location.reload();


    }
  }, [selectedProfile, currentProfile, onProfileUpdate, handleClose]);

  const handleFinish = useCallback(() => {
    const profile = selectedProfile;
    handleClose();
    if (onFinish) onFinish(profile);
  }, [handleClose, onFinish, selectedProfile]);


  const getCurrentProfileLabel = () => getProfileLabel(currentProfile);

  const renderProfileStep = () => (
    <>
      <Typography
        variant="h5"
        sx={{
          color: "#7B1FA2",
          fontWeight: 700,
          mb: 2,
          fontSize: { xs: "18px", sm: "22px" },
        }}
      >
        Vamos personalizar sua experiência?
      </Typography>

      <Typography
        variant="body1"
        sx={{
          color: "#424242",
          mb: 3,
          fontSize: { xs: "14px", sm: "16px" },
          lineHeight: 1.6,
        }}
      >{currentProfile ?
        <span>No momento, você está como <strong>{getCurrentProfileLabel()}</strong>.
        Atualize seu perfil para garantir acesso a todas as recomendações e
        recursos certos para você.</span> : "Atualize o seu perfil para garantir acesso a todas as recomendações e recursos da plataforma."
      }</Typography>

      {/* Desktop: Cards lado a lado / Mobile: Cards com ícone à esquerda */}
      <Box
        sx={{
          display: { xs: "none", sm: "flex" },
          flexDirection: "row",
          gap: 2,
          mb: selectedProfile ? 4 : 0,
        }}
      >
        {profileOptions.map((option) => (
          <Box
            key={option.key}
            onClick={() => handleProfileSelect(option.key)}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "flex-start",
              padding: "24px 16px",
              border: selectedProfile === option.key ? "2px solid #7B1FA2" : "1px solid #E0E0E0",
              borderRadius: "8px",
              cursor: "pointer",
              transition: "all 0.2s ease",
              backgroundColor: "#FFFFFF",
              flex: 1,
              minHeight: "180px",
              "&:hover": {
                borderColor: "#7B1FA2",
                boxShadow: "0 2px 8px rgba(123, 31, 162, 0.15)",
              },
            }}
          >
            <Box
              component="img"
              src={option.icon}
              alt={option.title}
              sx={{
                width: 72,
                height: 72,
                mb: 2,
              }}
            />
            <Typography
              variant="subtitle1"
              sx={{
                color: "#F9A825",
                fontWeight: 700,
                textAlign: "center",
                mb: 1,
                fontSize: "16px",
              }}
            >
              {option.title}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "#757575",
                textAlign: "center",
                fontSize: "13px",
                lineHeight: 1.4,
              }}
            >
              {option.description}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Mobile: Cards empilhados com ícone à esquerda */}
      <Box
        sx={{
          display: { xs: "flex", sm: "none" },
          flexDirection: "column",
          gap: 1.5,
          mb: selectedProfile ? 3 : 0,
        }}
      >
        {profileOptions.map((option) => (
          <Box
            key={option.key}
            onClick={() => handleProfileSelect(option.key)}
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              padding: "16px",
              border: selectedProfile === option.key ? "2px solid #7B1FA2" : "1px solid #E0E0E0",
              borderRadius: "12px",
              cursor: "pointer",
              transition: "all 0.2s ease",
              backgroundColor: "#FFFFFF",
              gap: 2,
              "&:hover": {
                borderColor: "#7B1FA2",
              },
            }}
          >
            <Box
              component="img"
              src={option.icon}
              alt={option.title}
              sx={{
                width: 48,
                height: 48,
                flexShrink: 0,
              }}
            />
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  color: "#F9A825",
                  fontWeight: 700,
                  fontSize: "15px",
                  mb: 0.5,
                }}
              >
                {option.title}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#757575",
                  fontSize: "12px",
                  lineHeight: 1.3,
                }}
              >
                {option.description}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>

      {selectedProfile && (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <AButton
            variant="contained"
            disabled={isLoading}
            onClick={handleConfirm}
            sx={{
              backgroundColor: "#F9A825",
              color: "#FFFFFF",
              px: { xs: 4, sm: 6 },
              py: 1.5,
              fontSize: { xs: "14px", sm: "16px" },
              fontWeight: 600,
              textTransform: "uppercase",
              width: { xs: "100%", sm: "auto" },
              "&:hover": {
                backgroundColor: "#F57F17",
              },
            }}
          >
            {isLoading ? "Atualizando..." : "CONTINUAR"}
          </AButton>
        </Box>
      )}
    </>
  );

  const renderConfirmationStep = () => (
    <>
      <Typography
        variant="h5"
        sx={{
          color: "#7B1FA2",
          fontWeight: 700,
          mb: 3,
          fontSize: { xs: "18px", sm: "22px" },
        }}
      >
        Atualização concluída!
      </Typography>

      <Typography
        variant="body1"
        sx={{
          color: "#424242",
          mb: 4,
          lineHeight: 1.6,
          fontSize: { xs: "14px", sm: "16px" },
        }}
      >
        Tudo certo! Seu perfil foi atualizado com sucesso. Obrigado por manter
        suas informações em dia.
      </Typography>

      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <AButton
          variant="contained"
          onClick={handleFinish}
          sx={{
            backgroundColor: "#F9A825",
            color: "#FFFFFF",
            px: { xs: 4, sm: 8 },
            py: 1.5,
            fontSize: { xs: "14px", sm: "16px" },
            fontWeight: 600,
            textTransform: "uppercase",
            width: { xs: "100%", sm: "auto" },
            "&:hover": {
              backgroundColor: "#F57F17",
            },
          }}
        >
          FECHAR
        </AButton>
      </Box>
    </>
  );

  return (
    <Modal
      open={open}
      onClose={handleClose}
      closeAfterTransition
      disableScrollLock
    >
      <Fade in={open} timeout={300}>
        <ModalBox>
          <Zoom in={open} style={{ transitionDelay: open ? "100ms" : "0ms" }}>
            <Card
              sx={{
                borderRadius: { xs: "12px", sm: "16px" },
                overflow: "auto",
                position: "relative",
                border: "3px solid #F9A825",
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.15)",
                maxHeight: { xs: "85vh", sm: "90vh" },
              }}
            >
              <CardContent sx={{ p: { xs: 2.5, sm: 5 }, pb: { xs: 3, sm: 5 } }}>
                {/* Stepper */}
                <Box sx={{ mb: { xs: 3, sm: 4 } }}>
                  <Stepper
                    activeStep={activeStep}
                    alternativeLabel
                    connector={<PurpleConnector />}
                  >
                    {steps.map((label) => (
                      <Step key={label}>
                        <StyledStepLabel>{label}</StyledStepLabel>
                      </Step>
                    ))}
                  </Stepper>
                </Box>

                {activeStep === STEP_PROFILE && renderProfileStep()}
                {activeStep === STEP_CONFIRMATION && renderConfirmationStep()}
              </CardContent>
            </Card>
          </Zoom>
        </ModalBox>
      </Fade>
    </Modal>
  );
};

export default ProfileUpdateModal;
